import { StripeAvailablePaymentMethod } from "../../enums";
import { OrganizationCountry } from "../enums/country";
import { AvailablePaymentCurrency } from "../enums/currency";
import { isIeRegisteredCharityNumberValid } from "./charity-registration";
import { DeLegalForm, formatDeRegisterNumberForStripe, getDeRegisterType } from "./de-fiscal";
import { isIeCroNumberValid } from "./ein";
export const getStripeProviderKeyFromCountry = (country) => {
    switch (country) {
        case OrganizationCountry.Canada:
            return "stripeCad";
        case OrganizationCountry.UnitedStates:
            return "stripeUsa";
        case OrganizationCountry.GreatBritain:
            return "stripeUk";
        case OrganizationCountry.Australia:
            return "stripeAu";
        case OrganizationCountry.Ireland:
        case OrganizationCountry.Germany:
            return "stripeEu";
    }
    throw new Error("Country was not provided on the Stripe provider");
};
const STRIPE_EU_PLATFORM_COUNTRIES = [OrganizationCountry.Ireland, OrganizationCountry.Germany];
export const getCountriesSharingStripePlatform = (country) => STRIPE_EU_PLATFORM_COUNTRIES.includes(country) ? STRIPE_EU_PLATFORM_COUNTRIES : [country];
export const doesStripePlatformRequireAccountToken = (country) => {
    return getStripeProviderKeyFromCountry(country) === "stripeEu";
};
export const getStripeApiKeyFromCountry = (country, stripeConfig) => {
    if (country === OrganizationCountry.Canada) {
        return stripeConfig.secretKeyCad;
    }
    if (country === OrganizationCountry.UnitedStates) {
        return stripeConfig.secretKeyUsa;
    }
    if (country === OrganizationCountry.GreatBritain) {
        return stripeConfig.secretKeyUk;
    }
    if (country === OrganizationCountry.Australia) {
        return stripeConfig.secretKeyAu;
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return stripeConfig.secretKeyEu;
    }
    throw new Error("Country was not provided on the Stripe provider");
};
export const getStripeFrontendApiKeyFromCountry = (country, stripePublicKeys) => {
    if (country === OrganizationCountry.Australia) {
        return stripePublicKeys.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_AU;
    }
    if (country === OrganizationCountry.Canada) {
        return stripePublicKeys.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_CA;
    }
    if (country === OrganizationCountry.GreatBritain) {
        return stripePublicKeys.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_UK;
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return stripePublicKeys.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_EU;
    }
    return stripePublicKeys.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_USA;
};
// These are test tokens provided by Stripe to be automatically approved in test mode
// More info: https://docs.stripe.com/connect/testing#creating-accounts
export const stripeTestModeTokens = {
    addressLine1: "address_full_match​",
    dob: { day: 1, month: 1, year: 1902 },
    taxId: "000000001",
    id_number: "222222222",
    fileIdentity: "file_identity_document_success",
    tos_acceptance: { date: 1609798905, ip: "8.8.8.8" }, // Coming from https://stackoverflow.com/a/70722112
};
export const getTestModeBankAccount = (country) => {
    switch (country) {
        case OrganizationCountry.Canada:
            return { country: "CA", currency: "cad", routing_number: "11000-000", account_number: "000123456789" };
        case OrganizationCountry.UnitedStates:
            return { country: "US", currency: "usd", routing_number: "110000000", account_number: "000123456789" };
        case OrganizationCountry.GreatBritain:
            return { country: "GB", currency: "gbp", routing_number: "108800", account_number: "00012345" };
        case OrganizationCountry.Australia:
            return { country: "AU", currency: "aud", routing_number: "110000", account_number: "000123456" };
        // SEPA country: the IBAN is the full account number, no separate routing number
        case OrganizationCountry.Ireland:
            return { country: "IE", currency: "eur", routing_number: "", account_number: "IE29AIBK93115212345678" };
        case OrganizationCountry.Germany:
            return { country: "DE", currency: "eur", routing_number: "", account_number: "DE89370400440532013000" };
        default:
            return { country: "US", currency: "usd", routing_number: "110000000", account_number: "000123456789" };
    }
};
export const getAddressAndPhoneForTestModeAutomateOnboardingToStripe = (country) => {
    switch (country) {
        case OrganizationCountry.Canada: {
            return {
                address: {
                    line1: stripeTestModeTokens.addressLine1,
                    city: "Toronto",
                    postal_code: "M4B 1B3",
                    country: "CA",
                    state: "ON",
                },
                phone: "+15062345678",
            };
        }
        case OrganizationCountry.UnitedStates: {
            return {
                address: {
                    line1: "123 State St", // stripeTestModeTokens.addressLine1 does not work for the US...
                    city: "Schenectady",
                    postal_code: "12345",
                    state: "NY",
                },
                phone: "+12015550123",
            };
        }
        case OrganizationCountry.GreatBritain: {
            return {
                address: {
                    line1: "123 State St",
                    city: "London",
                    postal_code: "EC1A 1BB",
                    country: "GB",
                    state: "London",
                },
                phone: "+442012345678",
            };
        }
        case OrganizationCountry.Australia: {
            return {
                address: {
                    line1: "123 State St",
                    city: "Sydney",
                    postal_code: "2000",
                    country: "AU",
                    state: "NSW",
                },
                phone: "+61212345678",
            };
        }
        case OrganizationCountry.NewZealand: {
            return {
                address: {
                    line1: "123 State St",
                    city: "Auckland",
                    postal_code: "1010",
                    country: "NZ",
                    state: "Auckland",
                },
                phone: "+64212345678",
            };
        }
        case OrganizationCountry.Ireland: {
            return {
                address: {
                    line1: "123 State St",
                    city: "Dublin",
                    postal_code: "D02 AF30",
                    country: "IE",
                    state: "Dublin",
                },
                phone: "+35312345678",
            };
        }
        case OrganizationCountry.Germany: {
            return {
                address: {
                    line1: "123 State St",
                    city: "Berlin",
                    postal_code: "10115",
                    country: "DE",
                },
                phone: "+493012345678",
            };
        }
    }
};
export const getPaymentMethodsList = (country) => {
    if (country === OrganizationCountry.UnitedStates) {
        return [StripeAvailablePaymentMethod.Card, StripeAvailablePaymentMethod.Ach, StripeAvailablePaymentMethod.CashApp];
    }
    if (country === OrganizationCountry.Canada) {
        return [StripeAvailablePaymentMethod.Card, StripeAvailablePaymentMethod.Pad];
    }
    if (country === OrganizationCountry.GreatBritain) {
        return [StripeAvailablePaymentMethod.Card];
    }
    if (country === OrganizationCountry.Australia) {
        return [StripeAvailablePaymentMethod.Card];
    }
    if (country === OrganizationCountry.Ireland) {
        return [StripeAvailablePaymentMethod.Card];
    }
    if (country === OrganizationCountry.Germany) {
        return [StripeAvailablePaymentMethod.Card, StripeAvailablePaymentMethod.Sepa];
    }
    return [];
};
const CLOVER_API_VERSION = "2025-12-15.clover";
const DAHLIA_API_VERSION = "2026-05-27.dahlia";
const STRIPE_WEBHOOK_API_VERSION_DEFAULT = "2025-09-30.preview";
export const getStripeWebhookExpectedApiVersion = (organizationCountry) => {
    if (organizationCountry === OrganizationCountry.Ireland || organizationCountry === OrganizationCountry.Germany) {
        return DAHLIA_API_VERSION;
    }
    if (organizationCountry === OrganizationCountry.Australia) {
        return CLOVER_API_VERSION;
    }
    return STRIPE_WEBHOOK_API_VERSION_DEFAULT;
};
export const needToCheckPaymentDisabled = (country) => {
    return country === OrganizationCountry.Canada;
};
export const getSetupIntentParams = (country, online) => {
    if (country === OrganizationCountry.UnitedStates) {
        return {
            mandate_data: {
                customer_acceptance: {
                    type: "online",
                    online,
                },
            },
        };
    }
    return {};
};
export function isAcssDebitSupportedCurrency(currency) {
    return currency === AvailablePaymentCurrency.Cad || currency === AvailablePaymentCurrency.Usd;
}
const isDeGovernmentEntity = (organization) => organization.country === OrganizationCountry.Germany &&
    organization.deLegalForm === DeLegalForm.KoerperschaftDesOeffentlichenRechts;
export const getStripeBusinessType = (organization) => isDeGovernmentEntity(organization) ? "government_entity" : "non_profit";
export const isStripeUboAndDirectorExempt = (organization) => isDeGovernmentEntity(organization);
const DE_LEGAL_FORM_STRIPE_STRUCTURES = {
    [DeLegalForm.EingetragenerVerein]: "incorporated_non_profit",
    [DeLegalForm.Ggmbh]: "incorporated_non_profit",
    [DeLegalForm.Gug]: "incorporated_non_profit",
    [DeLegalForm.Genossenschaft]: "incorporated_non_profit",
    [DeLegalForm.Stiftung]: "unincorporated_non_profit",
    [DeLegalForm.KoerperschaftDesOeffentlichenRechts]: "governmental_unit",
};
const hasIeCroNumber = (charityNumber) => Boolean(charityNumber?.trim()) && isIeCroNumberValid(charityNumber);
const isIeUnincorporatedCharity = (organization) => !hasIeCroNumber(organization.charityNumber) &&
    isIeRegisteredCharityNumberValid(organization.ieRegisteredCharityNumber);
export const getStripeCompanyStructure = (organization) => {
    if (organization.country === OrganizationCountry.GreatBritain) {
        return organization.charityNumber ? undefined : "unincorporated_non_profit";
    }
    if (organization.country === OrganizationCountry.Australia) {
        return "unincorporated_association";
    }
    if (organization.country === OrganizationCountry.Ireland) {
        return isIeUnincorporatedCharity(organization) ? "unincorporated_non_profit" : "incorporated_non_profit";
    }
    if (organization.country === OrganizationCountry.Germany) {
        return organization.deLegalForm ? DE_LEGAL_FORM_STRIPE_STRUCTURES[organization.deLegalForm] : undefined;
    }
    return undefined;
};
const DE_MEMBER_BASED_LEGAL_FORMS = [
    DeLegalForm.EingetragenerVerein,
    DeLegalForm.Stiftung,
    DeLegalForm.Genossenschaft,
];
export const getStripeRepresentativeRelationship = (organization) => {
    if (isStripeUboAndDirectorExempt(organization)) {
        return { representative: true, owner: false, director: false };
    }
    if (organization.country === OrganizationCountry.Germany &&
        organization.deLegalForm &&
        DE_MEMBER_BASED_LEGAL_FORMS.includes(organization.deLegalForm)) {
        return { representative: true, owner: false, director: true };
    }
    return { representative: true, owner: true, director: false };
};
export const getStripeCompanyTaxId = (organization) => {
    if (!organization.charityNumber) {
        return undefined;
    }
    if (organization.country === OrganizationCountry.UnitedStates) {
        return organization.charityNumber.replace(/-/g, "").padStart(9, "0");
    }
    if (organization.country === OrganizationCountry.Ireland) {
        return organization.charityNumber.trim().toUpperCase();
    }
    return organization.charityNumber;
};
// Only Ireland needs tax_id in the account token (CRO registry check); Germany goes through getStripeAccountTokenCompanyFields.
export const getStripeAccountTokenTaxId = (organization) => {
    if (organization.country !== OrganizationCountry.Ireland) {
        return undefined;
    }
    if (!hasIeCroNumber(organization.charityNumber)) {
        return undefined;
    }
    return getStripeCompanyTaxId(organization);
};
export const getStripeAccountTokenCompanyFields = (organization) => {
    if (organization.country === OrganizationCountry.Ireland) {
        const taxId = getStripeAccountTokenTaxId(organization);
        return taxId ? { tax_id: taxId } : {};
    }
    if (organization.country === OrganizationCountry.Germany) {
        if (!getDeRegisterType(organization.deLegalForm)) {
            return {};
        }
        const registerNumber = formatDeRegisterNumberForStripe(organization.charityNumber);
        return registerNumber ? { tax_id: registerNumber } : {};
    }
    return {};
};
export const getStripeCustomPaymentMethodIdFromCountry = (country, idsByCountry) => {
    switch (country) {
        case OrganizationCountry.Canada:
            return idsByCountry.canada;
        case OrganizationCountry.UnitedStates:
            return idsByCountry.unitedStates;
        case OrganizationCountry.GreatBritain:
            return idsByCountry.greatBritain;
        case OrganizationCountry.Australia:
            return idsByCountry.australia;
        case OrganizationCountry.Ireland:
            return idsByCountry.ireland;
        case OrganizationCountry.Germany:
            return idsByCountry.germany;
        default:
            return undefined;
    }
};
